import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Breadcrumb, Button, Descriptions, Form, Input, Modal, Select, Space, Table, message } from 'antd';
import { EditOutlined, EyeOutlined, HomeOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../api';

const AccommodationManagementPage = () => {
  const [students, setStudents] = useState([]);
  const [dormitories, setDormitories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [buildingFilter, setBuildingFilter] = useState();
  const [genderFilter, setGenderFilter] = useState();
  const [schoolFilter, setSchoolFilter] = useState();
  const [majorFilter, setMajorFilter] = useState();
  const [classFilter, setClassFilter] = useState();
  const [dormitoryFilter, setDormitoryFilter] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignmentSearchText, setAssignmentSearchText] = useState('');
  const [assignmentMatches, setAssignmentMatches] = useState([]);
  const [assignmentStudent, setAssignmentStudent] = useState(null);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const selectedAssignmentDormitoryId = Form.useWatch('dormitoryId', assignForm);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accommodation/students');
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setStudents([]);
      message.error('获取住宿学员失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchDormitories = async () => {
    try {
      const response = await api.get('/accommodation/dormitories');
      setDormitories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDormitories([]);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchDormitories();
  }, []);

  const buildings = useMemo(() => [...new Set(students.map(student => student.building).filter(Boolean))], [students]);
  const genders = useMemo(() => [...new Set(students.map(student => student.gender).filter(Boolean))], [students]);
  const schools = useMemo(() => [...new Set(students.map(student => student.school).filter(Boolean))], [students]);
  const majors = useMemo(() => [...new Set(students.map(student => student.major).filter(Boolean))], [students]);
  const classes = useMemo(() => [...new Set(students.map(student => student.className).filter(Boolean))], [students]);
  const dormitoryNumbers = useMemo(() => [...new Set(students.map(student => student.dormitoryNumber).filter(Boolean))], [students]);
  const bedNumbers = useMemo(() => [...new Set(students.map(student => student.bedNumber).filter(Boolean))], [students]);

  const filteredStudents = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    return students.filter(student => {
      const isAssigned = student.building && student.dormitoryNumber && student.bedNumber;
      const matchesSearch = !keyword || [student.name, student.account, student.building, student.dormitoryNumber, student.bedNumber]
        .some(value => String(value || '').toLowerCase().includes(keyword));
      return isAssigned && matchesSearch
        && (!buildingFilter || student.building === buildingFilter)
        && (!genderFilter || student.gender === genderFilter)
        && (!schoolFilter || student.school === schoolFilter)
        && (!majorFilter || student.major === majorFilter)
        && (!classFilter || student.className === classFilter)
        && (!dormitoryFilter || student.dormitoryNumber === dormitoryFilter);
    });
  }, [students, searchText, buildingFilter, genderFilter, schoolFilter, majorFilter, classFilter, dormitoryFilter]);

  const exportSelectedStudents = () => {
    const selectedStudents = filteredStudents.filter(student => selectedRowKeys.includes(student.id));
    if (!selectedStudents.length) {
      message.warning('请先选择学员');
      return;
    }
    const headers = ['姓名', '账号', '性别', '学校', '专业', '班级', '楼栋', '寝室号', '床位号'];
    const rows = selectedStudents.map(student => [student.name, student.account, student.gender, student.school, student.major, student.className, student.building, student.dormitoryNumber, student.bedNumber]);
    const csv = [headers, ...rows].map(row => row.map(value => `"${String(value || '').replaceAll('"', '""')}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = '住宿学员.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const availableDormitories = useMemo(() => dormitories.filter(
    dormitory => dormitory.residents.length < dormitory.bedCapacity
  ), [dormitories]);

  const selectedAssignmentDormitory = dormitories.find(dormitory => dormitory.id === selectedAssignmentDormitoryId);
  const assignmentBedOptions = selectedAssignmentDormitory
    ? Array.from({ length: selectedAssignmentDormitory.bedCapacity }, (_, index) => `${index + 1}号床`)
    : [];

  const openEdit = student => {
    setEditingStudent(student);
    form.setFieldsValue({
      building: student.building,
      dormitoryNumber: student.dormitoryNumber,
      bedNumber: student.bedNumber,
    });
    setEditOpen(true);
  };

  const resetAssignment = student => {
    setAssignmentSearchText(student?.name || '');
    setAssignmentMatches(student ? [student] : []);
    setAssignmentStudent(student || null);
    assignForm.resetFields();
  };

  const openAssignment = student => {
    resetAssignment(student);
    setAssignOpen(true);
  };

  const searchAssignmentStudent = value => {
    const keyword = value.trim().toLowerCase();
    setAssignmentSearchText(value);
    if (!keyword) {
      setAssignmentMatches([]);
      setAssignmentStudent(null);
      return;
    }
    const matches = students.filter(student => [student.name, student.account]
      .some(field => String(field || '').toLowerCase().includes(keyword)));
    setAssignmentMatches(matches);
    setAssignmentStudent(matches.length === 1 ? matches[0] : null);
  };

  const saveAccommodation = async () => {
    try {
      const values = await form.validateFields();
      await api.put(`/accommodation/students/${editingStudent.id}`, values);
      message.success('住宿信息已更新');
      setEditOpen(false);
      form.resetFields();
      await Promise.all([fetchStudents(), fetchDormitories()]);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || '更新住宿信息失败');
    }
  };

  const saveAssignment = async () => {
    if (!assignmentStudent) {
      message.warning('请先查询并选择学员');
      return;
    }
    try {
      const values = await assignForm.validateFields();
      const dormitory = dormitories.find(item => item.id === values.dormitoryId);
      await api.put(`/accommodation/students/${assignmentStudent.id}`, {
        building: dormitory.building,
        dormitoryNumber: dormitory.dormitoryNumber,
        bedNumber: values.bedNumber,
      });
      message.success(`${assignmentStudent.name} 已分配至 ${dormitory.building}-${dormitory.dormitoryNumber}`);
      setAssignOpen(false);
      resetAssignment();
      await Promise.all([fetchStudents(), fetchDormitories()]);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || '分配寝室失败');
    }
  };

  const columns = [
    { title: '姓名', dataIndex: 'name', key: 'name', width: 110 },
    { title: '账号', dataIndex: 'account', key: 'account', width: 130 },
    { title: '性别', dataIndex: 'gender', key: 'gender', width: 80, render: value => value || '未设置' },
    { title: '学校', dataIndex: 'school', key: 'school', width: 160, render: value => value || '-' },
    { title: '专业', dataIndex: 'major', key: 'major', width: 150, render: value => value || '-' },
    { title: '班级', dataIndex: 'className', key: 'className', width: 150, render: value => value || '-' },
    { title: '楼栋', dataIndex: 'building', key: 'building', width: 110, render: value => value || '-' },
    { title: '寝室号', dataIndex: 'dormitoryNumber', key: 'dormitoryNumber', width: 120, render: value => value || '-' },
    { title: '床位号', dataIndex: 'bedNumber', key: 'bedNumber', width: 110, render: value => value || '-' },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => <Space size={0}>
        <Button type="text" icon={<EyeOutlined />} aria-label={`查看 ${record.name} 的住宿信息`} onClick={() => setViewingStudent(record)} />
        <Button type="text" icon={<EditOutlined />} aria-label={`编辑 ${record.name} 的住宿信息`} onClick={() => openEdit(record)} />
      </Space>,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '住宿学员管理' },
        ]} />
        <h2 className="page-title">住宿学员管理</h2>
      </div>

      <div className="table-card">
        <Space wrap style={{ marginBottom: 20 }}>
          <Input allowClear prefix={<SearchOutlined />} placeholder="搜索姓名、账号、寝室号" value={searchText} onChange={event => setSearchText(event.target.value)} style={{ width: 240 }} />
          <Select allowClear placeholder="筛选楼栋" value={buildingFilter} onChange={setBuildingFilter} style={{ width: 160 }} options={buildings.map(building => ({ label: building, value: building }))} />
          <Select allowClear placeholder="筛选性别" value={genderFilter} onChange={setGenderFilter} style={{ width: 120 }} options={genders.map(value => ({ label: value, value }))} />
          <Select allowClear placeholder="筛选学校" value={schoolFilter} onChange={setSchoolFilter} style={{ width: 150 }} options={schools.map(value => ({ label: value, value }))} />
          <Select allowClear placeholder="筛选专业" value={majorFilter} onChange={setMajorFilter} style={{ width: 150 }} options={majors.map(value => ({ label: value, value }))} />
          <Select allowClear placeholder="筛选班级" value={classFilter} onChange={setClassFilter} style={{ width: 130 }} options={classes.map(value => ({ label: value, value }))} />
          <Select allowClear placeholder="筛选寝室号" value={dormitoryFilter} onChange={setDormitoryFilter} style={{ width: 130 }} options={dormitoryNumbers.map(value => ({ label: value, value }))} />
          <Button type="primary" onClick={() => openAssignment()}>分配寝室</Button>
          <Button disabled={!selectedRowKeys.length} onClick={exportSelectedStudents}>导出所选（{selectedRowKeys.length}）</Button>
        </Space>
        <Table rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }} columns={columns} dataSource={filteredStudents} loading={loading} rowKey="id" scroll={{ x: 1300 }} pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: total => `共 ${total} 名住宿学员` }} />
      </div>

      <Modal title={`编辑住宿信息 - ${editingStudent?.name || ''}`} open={editOpen} onCancel={() => setEditOpen(false)} onOk={saveAccommodation} okText="保存">
        <Form form={form} layout="vertical">
          <Form.Item name="building" label="楼栋"><AutoComplete options={buildings.map(value => ({ value }))} filterOption><Input placeholder="选择或输入楼栋" /></AutoComplete></Form.Item>
          <Form.Item name="dormitoryNumber" label="寝室号"><AutoComplete options={dormitoryNumbers.map(value => ({ value }))} filterOption><Input placeholder="选择或输入寝室号" /></AutoComplete></Form.Item>
          <Form.Item name="bedNumber" label="床位号"><AutoComplete options={bedNumbers.map(value => ({ value }))} filterOption><Input placeholder="选择或输入床位号" /></AutoComplete></Form.Item>
        </Form>
      </Modal>

      <Modal title={`学员详情 - ${viewingStudent?.name || ''}`} open={Boolean(viewingStudent)} onCancel={() => setViewingStudent(null)} footer={null} width={640}>
        {viewingStudent && <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="姓名">{viewingStudent.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="账号">{viewingStudent.account || '-'}</Descriptions.Item>
          <Descriptions.Item label="性别">{viewingStudent.gender || '未设置'}</Descriptions.Item>
          <Descriptions.Item label="学校">{viewingStudent.school || '-'}</Descriptions.Item>
          <Descriptions.Item label="专业">{viewingStudent.major || '-'}</Descriptions.Item>
          <Descriptions.Item label="班级">{viewingStudent.className || '-'}</Descriptions.Item>
          <Descriptions.Item label="上课方式">{viewingStudent.classMode || '-'}</Descriptions.Item>
          <Descriptions.Item label="楼栋">{viewingStudent.building || '-'}</Descriptions.Item>
          <Descriptions.Item label="寝室号">{viewingStudent.dormitoryNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="床位号">{viewingStudent.bedNumber || '-'}</Descriptions.Item>
        </Descriptions>}
      </Modal>

      <Modal title="分配寝室" open={assignOpen} onCancel={() => { setAssignOpen(false); resetAssignment(); }} onOk={saveAssignment} okText="确认分配" width={620}>
        <Form layout="vertical">
          <Form.Item label="学员姓名">
            <Input.Search value={assignmentSearchText} onChange={event => setAssignmentSearchText(event.target.value)} onSearch={searchAssignmentStudent} placeholder="输入学员姓名后点击查询" enterButton="查询" />
          </Form.Item>
          {assignmentMatches.length > 1 && (
            <Form.Item label="查询结果">
              <Select placeholder="请选择学员" value={assignmentStudent?.id} onChange={id => setAssignmentStudent(assignmentMatches.find(student => student.id === id))} options={assignmentMatches.map(student => ({ value: student.id, label: `${student.name}（${student.account}）` }))} />
            </Form.Item>
          )}
        </Form>

        {assignmentStudent && <>
          <Descriptions title="学员基本信息" size="small" column={2} bordered style={{ marginBottom: 20 }}>
            <Descriptions.Item label="姓名">{assignmentStudent.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="账号">{assignmentStudent.account || '-'}</Descriptions.Item>
            <Descriptions.Item label="性别">{assignmentStudent.gender || '未设置'}</Descriptions.Item>
            <Descriptions.Item label="学校">{assignmentStudent.school || '-'}</Descriptions.Item>
            <Descriptions.Item label="班级">{assignmentStudent.className || '-'}</Descriptions.Item>
            <Descriptions.Item label="专业">{assignmentStudent.major || '-'}</Descriptions.Item>
            <Descriptions.Item label="上课方式">{assignmentStudent.classMode || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前住宿" span={2}>{[assignmentStudent.building, assignmentStudent.dormitoryNumber, assignmentStudent.bedNumber].filter(Boolean).join('-') || '未分配'}</Descriptions.Item>
          </Descriptions>
          <Form form={assignForm} layout="vertical">
            <Form.Item name="dormitoryId" label="寝室" rules={[{ required: true, message: '请选择寝室' }]}>
              <Select showSearch optionFilterProp="label" placeholder="选择未满员寝室" options={availableDormitories.map(dormitory => ({ value: dormitory.id, label: `${dormitory.building}-${dormitory.dormitoryNumber}（${dormitory.residents.length}/${dormitory.bedCapacity} 床）` }))} onChange={() => assignForm.setFieldValue('bedNumber', undefined)} />
            </Form.Item>
            <Form.Item name="bedNumber" label="床位号" rules={[{ required: true, message: '请选择或输入床位号' }]}>
              <AutoComplete options={assignmentBedOptions.map(value => ({ value }))} filterOption><Input placeholder="选择或输入床位号" disabled={!selectedAssignmentDormitory} /></AutoComplete>
            </Form.Item>
          </Form>
        </>}
      </Modal>
    </div>
  );
};

export default AccommodationManagementPage;
